const DEFAULT_OG_IMAGE = 'https://dubaipetsgroomer.com/faviconDPG.png';
const SITE_NAME = 'Dubai Pets Groomer';

function upsertMeta(attr, key, content) {
  if (!content) return;

  let element = document.querySelector(`meta[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;

  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function absoluteUrl(path = '/') {
  if (typeof window === 'undefined') {
    return `https://dubaipetsgroomer.com${path}`;
  }
  return `${window.location.origin}${path}`;
}

export function updateSeoTags(route) {
  const title = route.meta?.title || SITE_NAME;
  const description = route.meta?.description || '';
  const canonicalPath = route.path.endsWith('/') || route.path === '/'
    ? route.path
    : `${route.path}${route.path.includes('.') ? '' : ''}`;
  const canonical = absoluteUrl(route.fullPath || canonicalPath);
  const ogImage = route.meta?.ogImage || DEFAULT_OG_IMAGE;
  const ogType = route.meta?.ogType || 'website';

  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonical);

  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', ogImage);
  upsertMeta('property', 'og:type', ogType);
  upsertMeta('property', 'og:site_name', SITE_NAME);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', ogImage);

  let robotsMeta = document.querySelector('meta[name="robots"]');
  if (route.meta?.noindex) {
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex, nofollow');
  } else if (robotsMeta) {
    robotsMeta.remove();
  }
}

export function upsertJsonLd(id, data) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export function removeJsonLd(id) {
  const script = document.getElementById(id);
  if (script) script.remove();
}

export function buildLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    url: 'https://dubaipetsgroomer.com/',
    logo: DEFAULT_OG_IMAGE,
    image: DEFAULT_OG_IMAGE,
    telephone: '+971522809076',
    email: 'dubaipetsgroomer@gmail.com',
    priceRange: 'AED',
    areaServed: {
      '@type': 'City',
      name: 'Dubai',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Dubai',
      addressCountry: 'AE',
    },
    // Hours to be confirmed by client before go-live; Mon–Sat daytime placeholder
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '21:00',
      },
    ],
    sameAs: [
      'https://www.facebook.com/dubaipetsgroomer/',
      'https://www.instagram.com/dubaipetsgroomer/',
      'https://www.twitter.com/dubaipetsgroomer/',
      'https://www.google.com/maps/place/Dubai+Pets+Groomer',
    ],
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function buildServiceSchema({ name, description, url, offers = [] }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: absoluteUrl(url),
    provider: {
      '@type': 'LocalBusiness',
      name: SITE_NAME,
      telephone: '+971522809076',
      url: 'https://dubaipetsgroomer.com/',
    },
    areaServed: {
      '@type': 'City',
      name: 'Dubai',
    },
    offers: offers.map((offer) => ({
      '@type': 'Offer',
      name: offer.name,
      price: String(offer.price ?? ''),
      priceCurrency: 'AED',
      availability: 'https://schema.org/InStock',
    })),
  };
}

export function buildFaqSchema(faqs = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function buildBlogPostingSchema(blog, path) {
  const image = blog.image
    ? (String(blog.image).startsWith('http') ? blog.image : `https://admin.dubaipetsgroomer.com/${blog.image}`)
    : DEFAULT_OG_IMAGE;

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blog.meta_title || blog.title,
    description: blog.meta_description || '',
    image,
    datePublished: blog.created_at || blog.published_at || undefined,
    dateModified: blog.updated_at || blog.created_at || undefined,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
      },
    },
    mainEntityOfPage: absoluteUrl(path),
  };
}

export function applyServicePageSchema({ name, description, path, offers, faqs }) {
  upsertJsonLd('ld-service', buildServiceSchema({ name, description, url: path, offers }));
  if (faqs?.length) {
    upsertJsonLd('ld-faq', buildFaqSchema(faqs));
  }
}

export function clearPageSpecificSchema() {
  removeJsonLd('ld-service');
  removeJsonLd('ld-faq');
  removeJsonLd('ld-blog');
}

export function trackEvent(eventName, params = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
}

export const WHATSAPP_URL =
  'https://wa.me/971522809076?text=' +
  encodeURIComponent('Hello, I am Looking for Grooming Services');

export const PHONE_TEL = 'tel:+971522809076';
export const EMAIL_MAILTO = 'mailto:dubaipetsgroomer@gmail.com';
